import { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { datadogLogs } from '@datadog/browser-logs';
import useGentian from './useGentian';
import { useLocation } from 'react-router-dom';

export interface DecodedToken extends Required<Omit<JwtPayload, 'nbf' | 'jti'>> {
  azp: string;
  permissions: string[];
  scope: string;
  sub: string;
}

export const useUser = () => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    logout,
    loginWithRedirect,
    isLoading: isAuthLoading,
    user,
  } = useAuth0();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const { mutate: updateAccount } = useGentian().updateAccount;
  const location = useLocation();

  // --------------------------
  // 1️⃣ Handle access_denied from Auth0
  // --------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    if (error === 'access_denied') {
      console.warn('Dev login blocked:', errorDescription);
      // Redirect blocked users to the landing page
      window.location.href = 'https://gop.gentian.io/';
    }
  }, [location]);

  // --------------------------
  // 2️⃣ Fetch permissions and decode token
  // --------------------------
  const fetchPermissions = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setDecodedToken(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      datadogLogs.logger.info('Fetching user permissions...');

      const accessToken = await getAccessTokenSilently();
      datadogLogs.logger.info('Access token retrieved');

      const decodedJwtToken = jwtDecode<DecodedToken>(accessToken);

      if (decodedJwtToken.exp < Date.now() / 1000) {
        await logout({ logoutParams: { returnTo: window.location.origin } });
        throw new Error('Token expired');
      }

      if (!decodedJwtToken.permissions || !Array.isArray(decodedJwtToken.permissions)) {
        throw new Error('Permissions not found in token or invalid format');
      }

      setDecodedToken(decodedJwtToken);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error fetching permissions';
      datadogLogs.logger.error('Error fetching user permissions', { error: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, logout]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (permission: string) => decodedToken?.permissions.includes(permission),
    [decodedToken],
  );

  // --------------------------
  // 3️⃣ Trigger login if not authenticated
  // --------------------------
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [loginWithRedirect, isAuthLoading, isAuthenticated]);

  // --------------------------
  // 4️⃣ Force email verification
  // --------------------------
  useEffect(() => {
    if (user && !user.email_verified) {
      alert('Please verify your email address before continuing. Check your spam folder.');
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [user, logout]);

  // --------------------------
  // 5️⃣ Update account metadata if necessary
  // --------------------------
  useEffect(() => {
    if (isLoading || isAuthLoading || !decodedToken || !user || !isAuthenticated) return;

    const update = async () => {
      updateAccount(
        {
          ...user,
          user_metadata: {
            name: user.name || 'Unknown User',
            jobTitle: '',
            country: '',
            company: '',
            industry: '',
            phoneNumber: '',
            companySize: '',
            termsAgreed: false,
            eulaAgreed: false,
            platformClient: true,
            reseller: false,
          },
        },
        {
          onSuccess: async () => {
            if (user && !user.email_verified) {
              alert(
                'Please verify your email address before continuing. Remember to check your spam folder.',
              );
            }
            await logout({ logoutParams: { returnTo: window.location.origin } });
          },
          onError: (error) => console.error(error),
        },
      );
    };

    if (user.email_verified && decodedToken.permissions && !hasPermission('create:projects')) {
      update();
    }
  }, [user, isAuthenticated, hasPermission, isAuthLoading, updateAccount, logout, decodedToken, isLoading]);

  return {
    clientId: decodedToken?.azp,
    permissions: decodedToken?.permissions,
    hasPermission,
    isLoading,
  };
};
